/**
 * Public Forms API unique-count field names.
 *
 * Docs (`docs.humblytics.com/external-analytics-api`) publish
 * `unique_submissions` on rows/trends and `summary.total_unique_submissions`.
 * The live API historically returned `unique_sessions` / `summary.unique_sessions`.
 * Clients built against either contract should resolve the same number.
 */

type FormsUniqueCountFields = {
  unique_submissions?: number | null;
  total_unique_submissions?: number | null;
  unique_sessions?: number | null;
};

const asCount = (value: number | null | undefined): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

/** Distinct sessions that submitted — documented name first, live API alias second. */
export const pickUniqueSubmissions = (
  row: FormsUniqueCountFields | null | undefined,
): number => {
  if (!row) return 0;
  return (
    asCount(row.unique_submissions) ??
    asCount(row.total_unique_submissions) ??
    asCount(row.unique_sessions) ??
    0
  );
};

const withUniqueAliases = <T extends FormsUniqueCountFields>(row: T): T => {
  const unique = pickUniqueSubmissions(row);
  return {
    ...row,
    unique_submissions: unique,
    unique_sessions: unique,
  };
};

/** Stamp documented + legacy unique-count aliases on a forms breakdown payload. */
export const normalizeFormsBreakdownResponse = <T>(raw: T): T => {
  const payload = raw as {
    data?: FormsUniqueCountFields[];
    trend?: FormsUniqueCountFields[];
    summary?: FormsUniqueCountFields & {
      groups?: Record<string, FormsUniqueCountFields>;
    };
  };

  const unique = pickUniqueSubmissions(payload.summary);
  const groups = payload.summary?.groups
    ? Object.fromEntries(
        Object.entries(payload.summary.groups).map(([key, group]) => [
          key,
          {
            ...withUniqueAliases(group),
            total_unique_submissions: pickUniqueSubmissions(group),
          },
        ]),
      )
    : payload.summary?.groups;

  return {
    ...payload,
    data: payload.data?.map(withUniqueAliases) ?? payload.data,
    trend: payload.trend?.map(withUniqueAliases) ?? payload.trend,
    summary: payload.summary
      ? {
          ...payload.summary,
          unique_sessions: unique,
          unique_submissions: unique,
          total_unique_submissions: unique,
          groups,
        }
      : payload.summary,
  } as T;
};

/** Stamp documented + legacy unique-count aliases on a forms details payload. */
export const normalizeFormsDetailsResponse = <T>(raw: T): T => {
  const payload = raw as {
    data?: { forms?: FormsUniqueCountFields[] };
    summary?: FormsUniqueCountFields;
  };
  const unique = pickUniqueSubmissions(payload.summary);
  return {
    ...payload,
    data: payload.data
      ? {
          ...payload.data,
          forms: payload.data.forms?.map(withUniqueAliases) ?? payload.data.forms,
        }
      : payload.data,
    summary: payload.summary
      ? {
          ...payload.summary,
          unique_sessions: unique,
          unique_submissions: unique,
          total_unique_submissions: unique,
        }
      : payload.summary,
  } as T;
};
